import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import {
  Cushion,
  FlexContainer,
  Grid,
  Rectangle,
  Shadow,
  Space,
  Typography,
} from '@pitchero/react-ui';
import { Trans } from '../../../lib/i18n';
import { SEASONS_PROP_TYPES } from '../../../lib/prop-types/season';
import ClubLink from '../../club-link';
import Nl2Br from '../../nl2br';
import Sponsor from '../../sponsors/sponsor';
import ProfileAvatar from './avatar';
import ProfileHeaderStats from './header-stats';
import ProfileHeaderStatsLoading from './header-stats-loading';
import SeasonFilter from '../season-filter';

const ProfileHeader = ({
  avatar,
  biography,
  id,
  isLoading,
  left,
  name,
  onChangeSeason,
  previousClubs,
  profileType,
  position,
  seasonId,
  seasons,
  sponsor,
  stats,
  teamId,
  teams,
}) => {
  const otherTeams = isEmpty(teams) ? [] : teams.filter((team) => team.id !== teamId);
  const showAdditionalInfo =
    !isEmpty(biography) || !isEmpty(previousClubs) || !isEmpty(otherTeams) || !isEmpty(sponsor);

  const handleSeasonChange = (event) => {
    onChangeSeason(parseInt(event.target.value, 10));
  };

  return (
    <>
      <Space bottom="medium" responsive={[{ minWidth: 'tab', props: { bottom: 'large' } }]}>
        <Grid preset="profileHeader">
          <ProfileAvatar avatar={avatar} name={name} hasLeft={!isEmpty(left)} />
          <div className="grid-profile-header__name" style={{ gridArea: 'name' }}>
            <Space top="0" bottom="small">
              <Typography preset="title--large" color="black" component="h1">
                {name}
              </Typography>
            </Space>
            {position && (
              <Typography preset="button" color="dustygrey">
                {position}
              </Typography>
            )}
          </div>
          {profileType === 'player' && (
            <>
              <div
                className="grid-profile-header__season"
                style={{ gridArea: 'season', alignSelf: 'start' }}
              >
                <SeasonFilter seasons={seasons} selected={seasonId} onChange={handleSeasonChange} />
              </div>
              {isLoading ? (
                <ProfileHeaderStatsLoading profileType={profileType} />
              ) : (
                <ProfileHeaderStats name={name} {...stats} />
              )}
            </>
          )}
        </Grid>
      </Space>

      {showAdditionalInfo && (
        <Space bottom="large">
          <Shadow>
            <Rectangle fill="white" radius="rounded">
              <Cushion all="large">
                <FlexContainer
                  flexDirection="row"
                  responsive={[{ maxWidth: 'tab', props: { flexDirection: 'column' } }]}
                >
                  {sponsor && (
                    <Space
                      right="large"
                      style={{ width: 244, maxWidth: '100%', flexShrink: 0 }}
                      responsive={[{ maxWidth: 'tab', props: { right: '0', bottom: 'large' } }]}
                    >
                      <Space bottom="small">
                        <Typography preset="name" component="h3">
                          <Trans i18nKey="teams:profile_header_sponsor">Player sponsor</Trans>
                        </Typography>
                      </Space>
                      <Sponsor {...sponsor} placement="player" />
                    </Space>
                  )}
                  <div>
                    {!isEmpty(otherTeams) && (
                      <Space bottom="medium">
                        <Space bottom="small">
                          <Typography preset="name" component="h3">
                            <Trans i18nKey="teams:profile_header_other_teams">Also plays for</Trans>
                          </Typography>
                        </Space>
                        {otherTeams.map((otherTeam, index) => (
                          <Space key={otherTeam.id} right="xsmall">
                            <ClubLink
                              route="team.squad.profile"
                              params={{
                                teamId: otherTeam.id,
                                profileType,
                                _profile: name,
                                profileId: id,
                              }}
                              scroll={false}
                              replace
                            >
                              <a>
                                <Typography preset="button" color="primary">
                                  {`${otherTeam.name}${
                                    index !== otherTeams.length - 1 ? ',' : ''
                                  } `}
                                </Typography>
                              </a>
                            </ClubLink>
                          </Space>
                        ))}
                      </Space>
                    )}
                    {previousClubs && (
                      <Space bottom="medium">
                        <Space bottom="small">
                          <Typography preset="name" component="h3">
                            <Trans i18nKey="teams:profile_header_previous_clubs">
                              Previous Clubs
                            </Trans>
                          </Typography>
                        </Space>
                        <Typography preset="body--large">{previousClubs}</Typography>
                      </Space>
                    )}
                    {biography && (
                      <Space bottom="medium">
                        <Space bottom="small">
                          <Typography preset="name" component="h3">
                            <Trans i18nKey="teams:profile_header_biography">Biography</Trans>
                          </Typography>
                        </Space>
                        <Typography preset="body--large">
                          <Nl2Br>{biography}</Nl2Br>
                        </Typography>
                      </Space>
                    )}
                  </div>
                </FlexContainer>
              </Cushion>
            </Rectangle>
          </Shadow>
        </Space>
      )}
    </>
  );
};

ProfileHeader.defaultProps = {
  biography: null,
  left: null,
  previousClubs: null,
  position: null,
  seasonId: null,
  sponsor: null,
  stats: null,
};

ProfileHeader.propTypes = {
  avatar: PropTypes.string.isRequired,
  biography: PropTypes.string,
  id: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  left: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChangeSeason: PropTypes.func.isRequired,
  previousClubs: PropTypes.string,
  profileType: PropTypes.oneOf(['player', 'coach']).isRequired,
  position: PropTypes.string,
  seasonId: PropTypes.number,
  seasons: SEASONS_PROP_TYPES.isRequired,
  sponsor: PropTypes.shape(),
  stats: PropTypes.shape(),
  teamId: PropTypes.number.isRequired,
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ).isRequired,
};

export default ProfileHeader;
